import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import { requestEntityRatingAndFetchDataAction } from 'store/entity/entityActions';
import { creditRatingScalesMapping } from 'shared/mappings';
import { ratingRange } from 'shared/constants/company';
import { portfolioTypes } from 'shared/constants/portfolioTypes';
import { endorsementCreditRating } from 'shared/constants/summaryTab';
import { BlurWrapper } from 'ui';
import { ScoreWidgetMainColumn } from 'ui/ScoreWidget';
import { SummaryCard, SummaryCardLineChart } from 'modules/common/SummaryCard';
import summaryTabHistoryData from 'assets/mockedData/summaryTabHistoryData.json';
import {
  getEntityDataProductsLevel,
  getEntityDataProductShowRatingRequestInProgress,
  getEntityDataProductShowRequestRatingBox,
} from 'selectors/entity';
import { shopProductsCategories } from 'modules/shop/shared/constants/shopProductsCategories';
import RequestContent from 'modules/common/SummaryCard/Request/RequestContent';
import { generalInformationMock } from 'modules/common/SummaryCard/Request/generalInformationMock';
import SummaryCardLockedRating from 'modules/common/SummaryCard/Request/SummaryCardLockedRating';

const data = {
  latestChange: {},
  generalInformation: {},
  levels: {},
  dates: {},
  documents: [],
  ratingScalesData: creditRatingScalesMapping(
    portfolioTypes.CREDIT_RATING,
    ratingRange.longTermRange,
  ),
  generalInformationList: generalInformationMock,
  endorsementList: endorsementCreditRating,
};

const useStyles = makeStyles(theme => ({
  root: {
    height: '100%',
  },
  content: {
    flexDirection: 'column',
    alignContent: 'center',
    justifyContent: 'center',
    height: '100%',
  },
  contentRow: {
    textAlign: 'center',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  link: {
    textDecoration: 'underline',
    cursor: 'pointer',
  },
}));

const SummaryCardRequestCreditRating = ({
  entityId,
  isAvailable,
  subscribed,
  data: relatedSubscriptionData,
  onUnlock,
  showCreditRatingNotAvailableMessage,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const level = useSelector(getEntityDataProductsLevel);
  const showRatingRequestInProgress = useSelector(getEntityDataProductShowRatingRequestInProgress);
  const showRequestRatingBox = useSelector(getEntityDataProductShowRequestRatingBox);

  const handleRequest = () => {
    dispatch(requestEntityRatingAndFetchDataAction({ id: entityId, level }));
  };

  const showRatingIsNotAvailable =
    (!showRequestRatingBox && !showRatingRequestInProgress) || !entityId || !isAvailable;

  const showRatingContent = showRatingIsNotAvailable ? (
    <RequestContent onRequest={handleRequest} isPending={showRatingRequestInProgress}>
      <Grid className={classes.contentRow}>
        <Typography display="block">
          {t('The credit rating for this company or instrument is not yet available.')}
        </Typography>
        <Typography display="block">
          {t(
            'If you are interested in the rating, please request it here. By requesting, you help us to prioritise our selection of ratings.',
          )}
        </Typography>
        <Typography display="block">
          {t('Once assigned, the rating and related information will be made available here.')}
        </Typography>
      </Grid>
    </RequestContent>
  ) : (
    <RequestContent onRequest={handleRequest} isPending={showRatingRequestInProgress}>
      <Grid className={classes.contentRow}>
        <Typography display="block">
          {t(
            'This entity has not been rated yet. If you are interested in the rating, please indicate your interest by clicking "Request" here. Once available you will be informed about the rating.',
          )}
        </Typography>
      </Grid>
    </RequestContent>
  );

  const creditRatingNotAvailableMessage = (
    <Grid className={classes.contentRow}>
      <Typography display="block">
        {t(
          'If you are interested in obtaining a credit rating for this entity, please contact your personal advisor or the service center.',
        )}
      </Typography>
    </Grid>
  );

  const content = subscribed ? (
    showRatingContent
  ) : (
    <SummaryCardLockedRating data={relatedSubscriptionData} onUnlock={onUnlock} />
  );

  return (
    <BlurWrapper
      content={
        <Grid container className={classes.content}>
          {showCreditRatingNotAvailableMessage ? creditRatingNotAvailableMessage : content}
        </Grid>
      }
      active
    >
      <SummaryCard
        headSquare={
          <ScoreWidgetMainColumn
            className={classes.root}
            label="Score"
            value="A"
            footer="Outlook: negative"
            footerColor="red"
          />
        }
        history={<SummaryCardLineChart history={summaryTabHistoryData} />}
        data={data}
      />
    </BlurWrapper>
  );
};

SummaryCardRequestCreditRating.defaultProps = {
  entityId: undefined,
  data: {
    category: shopProductsCategories.creditAnalysis,
    productLine: 'Rating',
    countriesAndIndustries: [],
  },
  onUnlock: () => {},
  showCreditRatingNotAvailableMessage: false,
};

SummaryCardRequestCreditRating.propTypes = {
  isAvailable: PropTypes.bool.isRequired,
  entityId: PropTypes.string,
  subscribed: PropTypes.bool.isRequired,
  data: PropTypes.shape({
    category: PropTypes.string,
    productLine: PropTypes.string,
    countriesAndIndustries: PropTypes.arrayOf(
      PropTypes.shape({
        country: PropTypes.string.isRequired,
        industries: PropTypes.arrayOf(PropTypes.string.isRequired),
      }),
    ),
  }),
  onUnlock: PropTypes.func,
  showCreditRatingNotAvailableMessage: PropTypes.bool,
};

export default SummaryCardRequestCreditRating;
